import { MapPin, Plus, MessageCircle, GitBranch, ListTodo, Users, Images } from 'lucide-react'
import { useChatPopup } from 'features/Chat'
import MdtLogo from 'images/MDT_FolderLogo.png'
import MobileNavCard from 'components/MobileNavCard/MobileNavCard'
import FieldShell from '../components/FieldShell'
import PrimerOfferBanner from '../components/PrimerOfferBanner'
import { fieldRoutes } from 'constants/fieldRoutes'

/**
 * The field app's front door.
 *
 * **A card list, in the language the rest of mobile MYDealTeams already
 * speaks.** The field app first shipped with navigation invented for it, which
 * made it look like a separate product bolted to the side of the tool — and
 * that seam is what "visually and thematically included" was pointing at.
 *
 * This replaces proximity as the landing screen, which reverses a Phase 0
 * decision ("Home is proximity, not a menu"). That reasoning was sound for a
 * capture-only app; it stops being sound once the field app has to carry tasks,
 * projects and partners as well. Near Me keeps a tab of its own, so the
 * proximity view is never more than one tap from anywhere.
 *
 * **Only sections that exist get a card.** A tile landing on "not built yet" is
 * worse than no tile — the dead Capture tab proved that on a home screen with
 * no back button. Tasks, Projects and Partners each add their own card in the
 * change that builds them.
 */
const FieldHome = () => {
	const chat = useChatPopup()

	return (
		<FieldShell
			/*
				The folder mark alone (MYD-532), not the name spelled out. Same
				asset the desktop rail and mobile header render, so the two halves
				of the product look like one. The alt text carries the accessible
				name for the `h1` it sits in.
			*/
			title={<img src={MdtLogo} alt="MYDealTeams" className="h-7 w-auto" />}
		>
			{/*
				Above the cards, because it is about getting the app working at all
				rather than about anything on them. It renders nothing once the app
				is installed or the offer has been dismissed, so for most users on
				most days this line costs a hook and no pixels.
			*/}
			<PrimerOfferBanner />

			<div className="flex flex-col gap-3 p-4">
				<MobileNavCard
					to={fieldRoutes.NEAR}
					icon={<MapPin className="w-6 h-6" />}
					label="Locations"
					hint="Your sites and locations, nearest first"
				/>
				<MobileNavCard
					to={fieldRoutes.NEW_SITE}
					icon={<Plus className="w-6 h-6" />}
					label="New Site"
					hint="Capture where you are standing"
				/>
				<MobileNavCard
					to={fieldRoutes.TASKS}
					icon={<ListTodo className="w-6 h-6" />}
					label="Tasks"
					hint="What is open, as a list or a month"
				/>
				<MobileNavCard
					to={fieldRoutes.PROJECTS}
					icon={<GitBranch className="w-6 h-6" />}
					label="Projects"
					hint="Notes, photos and chat, by project"
				/>
				<MobileNavCard
					to={fieldRoutes.PARTNERS}
					icon={<Users className="w-6 h-6" />}
					label="Partners"
					hint="Call or email the people on your deals"
				/>
				<MobileNavCard
					to={fieldRoutes.IMAGES}
					icon={<Images className="w-6 h-6" />}
					label="Images"
					hint="Capture and file images, for later use"
				/>
				<MobileNavCard
					onClick={() => chat.open()}
					icon={<MessageCircle className="w-6 h-6" />}
					label="Chats"
					hint="Messages, photos and voice notes"
				/>
			</div>
		</FieldShell>
	)
}

export default FieldHome
